import { Button, TrashIcon } from '@tunein/react-ui';
import { useContext, useState } from 'react';
import { removeRecent } from 'src/common/actions/browse';
import { showMessage } from 'src/common/actions/messagePopover';
import {
  CLEAR_RECENTS_ERROR,
  CLEAR_SINGLE_RECENT_BUTTON,
} from 'src/common/constants/localizations/settings';
import useActions from 'src/common/hooks/useActions';
import { LocationAndLocalizationContext } from '../../../../providers/LocationAndLocalizationProvider';
import type { GuideItemActionProps } from './GuideItemActions';
import css from './guide-item-actions.module.scss';

interface RemoveRecentActions {
  removeRecent: (guideId: string) => Promise<unknown>;
  showMessage: (message: string) => void;
}

/**
 * Removes a single item from recents. Only mounted when the API offers `actions.removeRecent` on
 * the item (see GuideItemActions).
 */
export function RemoveRecentAction({ guideItem }: GuideItemActionProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions<RemoveRecentActions>({ removeRecent, showMessage });
  const [isRemoving, setIsRemoving] = useState(false);

  // Platform sends the id to delete on the action itself; fall back to the item's own guideId.
  const guideId = guideItem?.actions?.removeRecent?.guideId || guideItem?.guideId;

  if (!guideId) {
    return null;
  }

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // The whole row is a link/click target, so keep the removal from also triggering navigation.
    event.preventDefault();
    event.stopPropagation();

    setIsRemoving(true);

    try {
      await actions.removeRecent(guideId);
      // On success the action re-fetches recents and this row unmounts, so there's no state to
      // settle here.
    } catch {
      setIsRemoving(false);
      actions.showMessage(CLEAR_RECENTS_ERROR);
    }
  };

  return (
    <Button
      aria-label={getLocalizedText(CLEAR_SINGLE_RECENT_BUTTON)}
      className={css.actionButton}
      data-testid="removeRecentAction"
      disabled={isRemoving}
      onClick={handleClick}
      type="button"
      variant="circle"
    >
      <TrashIcon className={css.actionIcon} />
    </Button>
  );
}
